import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { Toaster } from "./components/ui/sonner";
import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import Category from "./pages/Category";
import OrderDetails from "./pages/OrderDetails";
import Profile from "./pages/Profile";
import ResetPassword from "./pages/ResetPassword";
import Orders from "./pages/Orders";
import Settings from "./pages/Settings";
import Checkout from "./pages/Checkout";
import ThankYou from "./pages/thankyou";
import Wishlist from "./pages/Wishlist";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Categories from "./pages/Categories";
import Return from "./pages/Return";
import About from "./pages/About";
import Contact from "./pages/Contact";
import OrderTracking from "./pages/OrderTracking";
import ScrollToTop from "./ScrollToTop";
import Register from "./pages/Register";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import ShippingPolicy from "./pages/ShippingPolicy";
import ReturnPolicy from "./pages/ReturnPolicy";
import ReturnOrder from "./pages/returnorder";

// Admin
import AdminLogin from "./admin/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import AdminRoute from "./admin/AdminRoute";
import Dashboard from "./admin/Dashboard";
import AdminProducts from "./admin/AdminProducts";
import AdminOrders from "./admin/AdminOrders";
import AdminReturns from "./admin/AdminReturns";
import AdminCustomers from "./admin/AdminCustomers";
import AdminAnalytics from "./admin/AdminAnalytics";

const AppShell = ({ isAuth, children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  return (
    <div className="App">
      {!isAdmin && <Header isAuth={isAuth} />}
      {children}
      {!isAdmin && <Footer />}
      <Toaster />
    </div>
  );
};

function App() {
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuth(!!data.session);
    };
    checkSession();
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuth(!!session);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  return (
    <CartProvider>
      <WishlistProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppShell isAuth={isAuth}>
            <Routes>

              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/return/:orderId" element={<Return />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/track-order" element={<OrderTracking />} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/shipping-policy" element={<ShippingPolicy />} />
              <Route path="/return-policy" element={<ReturnPolicy />} />
              <Route path="/return-order" element={<ReturnOrder />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/order-details/:id" element={<OrderDetails />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/category/:category" element={<Category />} />
              <Route path="/category/:category/:subcategory" element={<Category />} />

              {/* Private routes */}
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="returns" element={<AdminReturns />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="analytics" element={<AdminAnalytics />} />
              </Route>

            </Routes>
          </AppShell>
        </BrowserRouter>
      </WishlistProvider>
    </CartProvider>
  );
}

export default App;